import React, { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { ContentCard } from '../../components/common/ContentCard';
import { Button } from '../../components/common/Button';
import { PAGE_COLORS } from '../../utils/pageColors';
import { useCreateJobMutation } from '../../store/slices/apiSlice';
import type { JobFormData, JobStatus, JobPriority, JobFulfillment, JobSource } from '../../utils/types';
import type { JobCreatePageProps } from '../../types';
import { ClipboardList, ArrowLeft } from 'lucide-react';
import { useToast } from '../../hooks/useToast';

const schema = z.object({
  source_type: z.enum(['manual', 'website']),
  total_items: z.coerce.number().int().nonnegative(),
  customer_first_name: z.string().min(1, 'First name is required'),
  customer_last_name: z.string().min(1, 'Last name is required'),
  customer_email: z.string().max(160).optional().or(z.literal('')).transform(v => v || null),
  customer_mobile_number: z.string().optional().or(z.literal('')).transform(v => v || null),
  delivery_address_line1: z.string().min(1, 'Delivery address line 1 is required'),
  delivery_address_line2: z.string().optional().or(z.literal('')).transform(v => v || null),
  delivery_city: z.string().optional().or(z.literal('')).transform(v => v || null),
  delivery_postcode: z.string().min(1, 'Delivery postcode is required'),
  delivery_notes: z.string().optional().or(z.literal('')).transform(v => v || null),
  invoice_address_line1: z.string().min(1, 'Invoice address line 1 is required'),
  invoice_address_line2: z.string().optional().or(z.literal('')).transform(v => v || null),
  invoice_city: z.string().optional().or(z.literal('')).transform(v => v || null),
  invoice_postcode: z.string().min(1, 'Invoice postcode is required'),
  delivery_user: z.string().uuid().optional().or(z.literal('')).transform(v => (v && v.length ? v : null)),
  total_amount: z.union([z.string(), z.number()]).transform(v => {
    const num = typeof v === 'string' ? parseFloat(v) : v;
    if (Number.isNaN(num)) throw new Error('Invalid amount');
    return Number(num.toFixed(2));
  }),
  status: z.enum(['new', 'in_progress', 'on_hold', 'completed', 'cancelled', 'production']),
  priority: z.enum(['low', 'normal', 'high', 'urgent']),
  fulfillment_type: z.enum(['delivery', 'collection', 'pallet']),
  job_reference: z.string().optional().or(z.literal('')).transform(v => v || null),
  website_job_number: z.string().optional().or(z.literal('')).transform(v => v || null),
  website_order_created_at: z.string().optional().or(z.literal('')).transform(v => v || null),
  admin_notes: z.string().optional().or(z.literal('')).transform(v => v || null),
});

export const JobCreatePage: React.FC<JobCreatePageProps> = ({ defaultData, className }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [createJob, { isLoading } ] = useCreateJobMutation();
  const [copyInvoiceToDelivery, setCopyInvoiceToDelivery] = useState(false);

  const formDefaultValues = useMemo(
    () => ({
      source_type: 'manual' as JobSource,
      total_items: 1,
      status: 'new' as JobStatus,
      priority: 'normal' as JobPriority,
      fulfillment_type: 'delivery' as JobFulfillment,
      ...defaultData,
    }),
    [defaultData]
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<JobFormData>({
    resolver: zodResolver(schema),
    defaultValues: formDefaultValues,
  });

  const onToggleCopy = () => {
    const invoice1 = watch('invoice_address_line1') || '';
    const invoice2 = watch('invoice_address_line2') || '';
    const invoiceCity = watch('invoice_city') || '';
    const invoicePc = watch('invoice_postcode') || '';
    setCopyInvoiceToDelivery(prev => !prev);
    if (!copyInvoiceToDelivery) {
      setValue('delivery_address_line1', invoice1);
      setValue('delivery_address_line2', invoice2 as any);
      setValue('delivery_city', invoiceCity as any);
      setValue('delivery_postcode', invoicePc);
    }
  };

  const onSubmit = async (values: JobFormData) => {
    try {
      const payload: JobFormData = {
        ...values,
        total_amount: typeof values.total_amount === 'number'
          ? values.total_amount.toFixed(2)
          : Number(parseFloat(values.total_amount as unknown as string).toFixed(2)).toString(),
      } as unknown as JobFormData;

      const res = await createJob(payload).unwrap();
      toast.success('Job Created', 'The job has been created successfully. Add orders next.');
      navigate(`/jobs/${res.id}/edit`);
    } catch (e: any) {
      const message = e?.data?.detail || 'Failed to create job. Please review your inputs and try again.';
      toast.error('Create Failed', message);
    }
  };

  return (
    <AppShell>
      <PageLayout
        title="Create Job"
        subtitle="Add a new job"
        bgColor={PAGE_COLORS.jobs.bgColor}
        textColor={PAGE_COLORS.jobs.textColor}
        borderColor={PAGE_COLORS.jobs.borderColor}
        icon={<ClipboardList className="w-6 h-6" />}
        className={className}
      >
        <div className="mb-4 flex items-center justify-between">
          <Link to="/jobs">
            <Button variant="ghost" size="sm" icon={<ArrowLeft className="w-4 h-4" />}>Back to Jobs</Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left column */}
          <div className="lg:col-span-2 space-y-4">
            {/* Job Meta */}
            <ContentCard title="Job Details">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Source</label>
                  <select className="form-input" {...register('source_type')}>
                    <option value="manual">Manual</option>
                    <option value="website">Website</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Website Job #</label>
                  <input className="form-input" placeholder="#10001" {...register('website_job_number')} />
                </div>
                <div>
                  <label className="form-label">Job Reference (internal)</label>
                  <input className="form-input" {...register('job_reference')} />
                </div>
              </div>
            </ContentCard>

            {/* Customer Details */}
            <ContentCard title="Customer Details">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">First Name</label>
                  <input className="form-input" {...register('customer_first_name')} />
                  {errors.customer_first_name && <p className="form-error">{errors.customer_first_name.message as string}</p>}
                </div>
                <div>
                  <label className="form-label">Last Name</label>
                  <input className="form-input" {...register('customer_last_name')} />
                  {errors.customer_last_name && <p className="form-error">{errors.customer_last_name.message as string}</p>}
                </div>
                <div>
                  <label className="form-label">Email</label>
                  <input className="form-input" placeholder="customer@example.com" {...register('customer_email')} />
                </div>
                <div>
                  <label className="form-label">Mobile</label>
                  <input className="form-input" placeholder="+44..." {...register('customer_mobile_number')} />
                </div>
              </div>
            </ContentCard>

            {/* Invoice Address */}
            <ContentCard title="Invoice Address">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Address Line 1</label>
                  <input className="form-input" {...register('invoice_address_line1')} />
                  {errors.invoice_address_line1 && <p className="form-error">{errors.invoice_address_line1.message as string}</p>}
                </div>
                <div>
                  <label className="form-label">Address Line 2</label>
                  <input className="form-input" {...register('invoice_address_line2')} />
                </div>
                <div>
                  <label className="form-label">City</label>
                  <input className="form-input" {...register('invoice_city')} />
                </div>
                <div>
                  <label className="form-label">Postcode</label>
                  <input className="form-input" {...register('invoice_postcode')} />
                  {errors.invoice_postcode && <p className="form-error">{errors.invoice_postcode.message as string}</p>}
                </div>
              </div>
            </ContentCard>

            {/* Delivery Address */}
            <ContentCard title="Delivery Address" subtitle="You can copy from invoice address">
              <div className="flex items-center justify-between mb-3">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" className="form-checkbox" checked={copyInvoiceToDelivery} onChange={onToggleCopy} />
                  Same as invoice address
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Address Line 1</label>
                  <input className="form-input" {...register('delivery_address_line1')} />
                  {errors.delivery_address_line1 && <p className="form-error">{errors.delivery_address_line1.message as string}</p>}
                </div>
                <div>
                  <label className="form-label">Address Line 2</label>
                  <input className="form-input" {...register('delivery_address_line2')} />
                </div>
                <div>
                  <label className="form-label">City</label>
                  <input className="form-input" {...register('delivery_city')} />
                </div>
                <div>
                  <label className="form-label">Postcode</label>
                  <input className="form-input" {...register('delivery_postcode')} />
                  {errors.delivery_postcode && <p className="form-error">{errors.delivery_postcode.message as string}</p>}
                </div>
                <div className="md:col-span-3">
                  <label className="form-label">Notes</label>
                  <textarea className="form-input" rows={3} {...register('delivery_notes')} />
                </div>
              </div>
            </ContentCard>

            {/* Order Summary */}
            <ContentCard title="Order Summary">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Total Items</label>
                  <input type="number" min={0} className="form-input" {...register('total_items', { valueAsNumber: true })} />
                  {errors.total_items && <p className="form-error">{errors.total_items.message as string}</p>}
                </div>
                <div>
                  <label className="form-label">Total Amount</label>
                  <input className="form-input" placeholder="111.23" {...register('total_amount')} />
                  {errors.total_amount && <p className="form-error">{errors.total_amount.message as string}</p>}
                </div>
              </div>
            </ContentCard>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <Button type="submit" onClick={handleSubmit(onSubmit)} disabled={isLoading}>Create Job</Button>
              <Link to="/jobs"><Button variant="ghost">Cancel</Button></Link>
            </div>
          </div>

          {/* Right column: Job Settings */}
          <div className="lg:col-span-1">
            <ContentCard title="Job Settings">
              <div className="space-y-4">
                <div>
                  <label className="form-label">Status</label>
                  <select className="form-input" {...register('status')}>
                    <option value="new">New</option>
                    <option value="in_progress">In Progress</option>
                    <option value="on_hold">On Hold</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Priority</label>
                  <select className="form-input" {...register('priority')}>
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Fulfillment</label>
                  <select className="form-input" {...register('fulfillment_type')}>
                    <option value="delivery">Delivery</option>
                    <option value="collection">Collection</option>
                    <option value="pallet">Pallet</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Admin Notes</label>
                  <textarea className="form-input" rows={6} {...register('admin_notes')} />
                </div>
              </div>
            </ContentCard>
          </div>
        </div>
      </PageLayout>
    </AppShell>
  );
};
